import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Save } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import toast from 'react-hot-toast';

interface SettingRow { key: string; value: string }

const settingsApi = {
  getAll: async (): Promise<SettingRow[]> => {
    const res = await api.get(API_ENDPOINTS.SETTINGS.LIST);
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : [];
  },
  update: async (key: string, value: string) => {
    await api.put(API_ENDPOINTS.SETTINGS.UPDATE(key), { value });
  },
};

const FIELDS = [
  {
    key: 'referral_share_base_url',
    label: 'Share Link Base URL',
    placeholder: 'https://refer.bookfleet.in/refer',
    hint: 'The referral code is appended, e.g. https://refer.bookfleet.in/refer/ABC123. Must point at a domain routed to this backend.',
  },
  {
    key: 'referral_banner_url',
    label: 'Share Preview Image',
    placeholder: 'https://refer.bookfleet.in/referral-banner.jpeg',
    hint: 'Shown as the link preview image (og:image) when the link is shared on WhatsApp/Telegram/etc.',
  },
  {
    key: 'referral_play_store_url',
    label: 'Play Store URL',
    placeholder: 'https://play.google.com/store/apps/details?id=com.ridezipto.customer',
    hint: 'Where the landing page sends people who don’t have the app installed.',
  },
] as const;

/**
 * Admin control for the URLs used in the referral share link + landing page.
 * Lets the domain shown to end users (currently the raw API host) be changed
 * to a branded one once it's DNS-routed to this backend, without a redeploy.
 */
export function ReferralSettingsCard() {
  const queryClient = useQueryClient();
  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: settingsApi.getAll,
  });

  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const map: Record<string, string> = {};
    settings.forEach((s) => { map[s.key] = s.value; });
    setValues({
      referral_share_base_url: map.referral_share_base_url ?? '',
      referral_banner_url: map.referral_banner_url ?? '',
      referral_play_store_url: map.referral_play_store_url ?? '',
    });
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const f of FIELDS) {
        await settingsApi.update(f.key, (values[f.key] ?? '').trim());
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('Referral link settings updated');
    },
    onError: () => toast.error('Failed to update referral link settings'),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <Link2 className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">Referral Share Link</h3>
            <p className="text-sm text-muted-foreground">
              Controls the domain and preview shown when a customer shares their referral link.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.key === 'referral_share_base_url' ? 'sm:col-span-2' : ''}>
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    className="mt-1"
                    placeholder={f.placeholder}
                    value={values[f.key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {saveMutation.isPending ? 'Saving…' : 'Save Link Settings'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
